import { useState } from 'react'
import MobileTabBar from './MobileTabBar'
import MobileMoreSheet from './MobileMoreSheet'
import Wordmark from '../Wordmark'
import ThemeToggle from '../ThemeToggle'

interface SyncState {
  syncing: boolean
  progress: { done: number; total: number } | null
  onSync: () => void
}

interface Props {
  children: React.ReactNode
  logoUrl: string | null
  isAdmin: boolean
  cadastroRecAvailable: boolean
  userEmail?: string | null
  sync: SyncState
  salesSyncing: boolean
  // Toast já vem renderizado pelo pai (AppLayout) — aqui só o shell.
}

/**
 * Shell mobile: header enxuto + conteúdo scrollável + bottom tab bar.
 * Renderizado por AppLayout quando useIsMobile() é true. O desktop nunca
 * passa por aqui.
 */
export default function MobileLayout({
  children,
  logoUrl,
  isAdmin,
  cadastroRecAvailable,
  userEmail,
  sync,
  salesSyncing,
}: Props) {
  const [moreOpen, setMoreOpen] = useState(false)

  return (
    <div className="h-dvh-screen bg-paper flex flex-col no-tap-highlight">
      {/* Header enxuto */}
      <header className="shrink-0 sticky top-0 z-30 bg-[#0D1B2A] border-b border-white/10 pt-safe">
        <div className="px-3 min-h-[52px] py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {logoUrl && (
              <img
                src={logoUrl}
                alt=""
                className="h-8 w-auto object-contain shrink-0"
              />
            )}
            <Wordmark showIcon={!logoUrl} iconClassName="h-8 w-8" textClassName="text-base" onDark />
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <ThemeToggle onDark />
            <button
              onClick={sync.onSync}
              disabled={sync.syncing}
              className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 bg-white/10 active:bg-white/20 disabled:opacity-50 rounded-lg text-xs text-white/90 touch-action-manip"
              aria-label="Sincronizar ERP"
            >
              {sync.syncing ? (
                <>
                  <span className="w-3 h-3 border border-white/60 border-t-transparent rounded-full animate-spin" />
                  {sync.progress && <span className="tabular-nums">{sync.progress.done}/{sync.progress.total}</span>}
                </>
              ) : (
                <span aria-hidden>⟳</span>
              )}
            </button>
          </div>
        </div>
        {salesSyncing && (
          <div className="px-3 pb-1.5 -mt-0.5">
            <span className="text-[10px] text-sky-300">Sincronizando vendas…</span>
          </div>
        )}
      </header>

      {/* Conteúdo — overflow-x-hidden impede que tabelas/toolbars antigas
          sangrem horizontalmente enquanto não migrarmos a tela pra cards. */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden mobile-scroll p-3">
        {children}
      </main>

      {/* Tab bar inferior */}
      <MobileTabBar onOpenMore={() => setMoreOpen(true)} moreActive={moreOpen} />

      <MobileMoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        isAdmin={isAdmin}
        cadastroRecAvailable={cadastroRecAvailable}
        userEmail={userEmail}
      />
    </div>
  )
}
